import {
  ArrowLeftOutlined,
  CloseOutlined,
  DownOutlined,
  HistoryOutlined,
  QuestionCircleOutlined
} from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Dropdown,
  MenuProps,
  Popover,
  Space,
  Tag,
  Tooltip
} from "antd";
import CiqAgSearch from "components/ciq-ag-search";
import { CiqIconBox } from "components/svg-icons";
import { ProjectContext } from "context/ProjectProvider";
import MaterialCreateCiqIcon from "components/svg-icons/material-create-ciq-icon";
import {
  FilterChipComponent,
  FilterChipComponentRef
} from "entity-app/shared-components/log-render-components/filter-chip-component";
import { resetGridFilters } from "entity-app/utils/utils";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { useHistory, useLocation } from "react-router";
import { getGridStatusBudge } from "components/ciq-log-page-header/grid-status-budge";
import AiPoweredUI from "components/welcome-startup-setup/ai-powered";
import AIPoweredIcon from "components/svg-icons/ai-powered-icon";
import MergeTrackIcon from "./icons/merge-track-icon";
import { ExtractSpecCardItem, MaterialExtractSpecType } from "../model";
import ImportExtractionDrawing from "./extract-upload-drawing-modal";
import CreateMaterialModal from "./create-material-modal";
import CreateMaterialsMultiModal from "./create-materials-multi-modal";
import MergeCreateMaterialModal from "./merge-create-material-modal";
import ExtractionHistoryGridModal from "./extraction-history-grid-modal";
import InfoCircleContent from "./info-circle-content";
import "./header-items.scss";
import { exportEntityLogAsCSV } from "entity-app/utils/export-entity-log-as-csv";
import useAuditEventLog from "entity-app/utils/export-audit-log";

type Prop = {
  gridRef: React.RefObject<AgGridReact<any>>;
  filterChipRef: React.RefObject<FilterChipComponentRef>;
  pageData: {
    entityList: MaterialExtractSpecType[] | undefined;
    materialHeaderMap: any;
    addMaterialsInLog: (
      selectedRows: MaterialExtractSpecType[]
    ) => Promise<void>;
    refetchExtractionHistoryData: () => void;
  };
  projectId: string;
  selectedRows: MaterialExtractSpecType[] | null;
  statsData:
    | {
        displayCount: number;
        filterName: undefined | string;
      }
    | undefined;
  isGridReady: boolean;
  selectedExtraction: MaterialExtractSpecType | undefined;
  extractionFilterId: string | undefined;
  onClosePdfViewer: () => void;
  extractionHistory: Array<ExtractSpecCardItem> | null;
};

export default function MaterialHeaderItems({
  gridRef,
  pageData,
  selectedRows,
  filterChipRef,
  statsData,
  projectId,
  isGridReady,
  selectedExtraction,
  extractionFilterId,
  onClosePdfViewer,
  extractionHistory
}: Prop) {
  const { state } = useLocation() as { state: any };
  const {
    isFeatureFlagEnabled,
    projectDetails,
    gqlClientForProject,
    isProjectArchived
  } = useContext(ProjectContext);
  const { saveAuditLogEvent } = useAuditEventLog(gqlClientForProject);

  const [openUploadDrawingModal, setOpenUploadDrawingModal] = useState(false);
  const [openCreateMaterial, setOpenCreateMaterial] = useState(false);
  const [openCreateMaterialsMulti, setOpenCreateMaterialsMulti] =
    useState(false);
  const [openMergeCreate, setOpenMergeCreate] = useState(false);
  const [openHistorySidebar, setOpenHistorySidebar] = useState(false);

  const history = useHistory();

  const isMaterialExtractionEnabled = isFeatureFlagEnabled(
    "MATERIAL_EXTRACTION"
  );

  useEffect(() => {
    if (state?.isOpenModel && !isProjectArchived) {
      setOpenUploadDrawingModal(true);
    }
  }, [state?.isOpenModel, isProjectArchived]);

  const onAllMaterialClick = useCallback(() => {
    resetGridFilters({
      setListFilter: () => {},
      api: gridRef.current?.api
    });
  }, [gridRef]);

  const materialsTypeMenu: MenuProps = useMemo(
    () => ({
      className: "max-w-sm",
      items: [
        {
          key: "material_log",
          onClick: () => history.replace(`/project/${projectId}/materials`),
          className: "px-4 py-2",
          label: (
            <div className="flex items-center space-x-3">
              <CiqIconBox feature="MATERIALS" size={20} padding={4} />
              <div>
                <div className="font-medium text-black text-opacity-90">
                  Material Log
                </div>
                <div className="text-ciq-gray-900 text-xs">
                  Plan and track Material delivery
                </div>
              </div>
            </div>
          )
        },
        isMaterialExtractionEnabled
          ? {
              key: "extracted_from_pdf",
              onClick: () =>
                history.replace(`/project/${projectId}/materials/extracted`),
              className: "px-4 py-2",
              label: (
                <div className="flex items-center justify-between space-x-3">
                  <div className="flex items-center space-x-3">
                    <CiqIconBox
                      feature="EXTRACTED_MATERIALS"
                      size={20}
                      padding={4}
                    />
                    <div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2 font-medium text-black text-opacity-90">
                          <span>Material Extraction</span>
                          <Tag>Beta</Tag>
                        </div>
                        <div className="shrink-0">
                          <AiPoweredUI />
                        </div>
                      </div>
                      <div className="text-ciq-gray-900 text-xs">
                        Extract Materials from Drawings and select the ones that
                        you want to track
                      </div>
                    </div>
                  </div>
                </div>
              )
            }
          : null
      ]
    }),
    [history, projectId, isMaterialExtractionEnabled]
  );

  const onExportCSV = useCallback(() => {
    if (gridRef.current && projectDetails) {
      const filename = `Extracted_Materials_${projectDetails.name}.csv`;
      exportEntityLogAsCSV({
        filename,
        gridRef: {
          api: gridRef.current.api
        },
        includedColumns: [],
        excludedColumns: ["active"]
      });

      saveAuditLogEvent({
        eventType: "MATERIAL_EXTRACTED_LOG_EXPORT_CSV",
        featureType: "EXTRACTED_MATERIALS",
        filename
      });
    }
  }, [saveAuditLogEvent, gridRef, projectDetails]);

  const actionSelectedButton = useMemo(() => {
    if (!selectedRows || selectedRows.length === 0) return null;
    const selectedCount = selectedRows.length;
    if (selectedCount === 0) return null;
    return (
      <div
        className="flex items-center rounded-md bg-ciq-gray-100/70 header-items"
        style={{ gap: 8, border: "none", padding: "8px 16px" }}
        key="MaterialLogPage_BulkEdit"
      >
        <Checkbox
          className="font-bold text-xs text-ciq-black/80 px-0"
          checked
          onChange={() => {
            gridRef.current?.api.deselectAll();
          }}
        >
          {selectedCount} Selected
        </Checkbox>
        <Button
          className="text-sm flex items-center space-x-1"
          disabled={!!isProjectArchived}
          onClick={() => {
            if (selectedCount === 1) {
              setOpenCreateMaterial(true);
            } else {
              setOpenCreateMaterialsMulti(true);
            }
          }}
        >
          <MaterialCreateCiqIcon
            size={13}
            className="fill-black"
            opacity="0.88"
          />
          <div>Track</div>
        </Button>
        {selectedCount > 1 && (
          <Button
            onClick={() => {
              setOpenMergeCreate(true);
            }}
            disabled={!!isProjectArchived}
            icon={<MergeTrackIcon size={16} />}
          >
            Merge &amp; Track
          </Button>
        )}
      </div>
    );
  }, [gridRef, selectedRows, isProjectArchived]);

  const headerItems = useMemo(() => {
    const menus = [] as JSX.Element[];

    const isShowSource = extractionFilterId !== undefined;

    if (!isShowSource && actionSelectedButton && isGridReady)
      menus.push(actionSelectedButton);

    if (!isShowSource && gridRef.current)
      menus.push(
        <FilterChipComponent
          key="MaterialLogPage_FilterChip"
          gridRef={gridRef}
          ref={filterChipRef}
        />
      );

    if (!isShowSource)
      menus.push(
        <CiqAgSearch
          key="MaterialLogPage_search"
          gridRef={gridRef}
          placeholder="Search Materials"
        />
      );
    if (!isShowSource)
      menus.push(
        <Tooltip
          title="Extraction History"
          key="MaterialLogPage_extractionHistory"
          placement="bottom"
        >
          <Button
            onClick={() => setOpenHistorySidebar(true)}
            className="flex items-center px-2"
          >
            <HistoryOutlined className="text-black text-opacity-90 text-xs" />
          </Button>
        </Tooltip>
      );

    if (!isShowSource)
      menus.push(
        <Button
          key="MaterialLogPage_export-csv"
          onClick={() => {
            onExportCSV();
          }}
          data-testid="material-header-export-csv"
          disabled={!!isProjectArchived}
          className="flex items-center space-x-2"
        >
          <div>Export CSV</div>
        </Button>
      );

    if (!isShowSource)
      menus.push(
        <Button
          key="MaterialLogPage_extraction"
          type="primary"
          onClick={() => {
            setOpenUploadDrawingModal(true);
          }}
          data-testid="material-header-extraction"
          disabled={!!isProjectArchived}
          className="flex items-center space-x-2"
        >
          <div>Extract from Drawings</div>
          <AIPoweredIcon className="fill-white" />
        </Button>
      );
    if (isShowSource) {
      menus.push(
        <Space
          className="flex items-center justify-between px-3 py-2 rounded-lg border border-solid border-ciq-blue-300 bg-ciq-blue-100"
          key="MaterialLogPage_viewingSources"
        >
          <div className="pr-4">
            Viewing Sources of{" "}
            <span className="font-bold">{selectedExtraction?.title}</span>
          </div>
          <CloseOutlined onClick={onClosePdfViewer} />
        </Space>
      );
    }

    menus.push(
      <Popover
        trigger={["hover"]}
        placement="bottomRight"
        content={<InfoCircleContent />}
      >
        <QuestionCircleOutlined
          size={16}
          className="text-ciq-gray-950-a80 cursor-pointer text-base m-2"
        />
      </Popover>
    );

    return menus;
  }, [
    extractionFilterId,
    actionSelectedButton,
    isGridReady,
    gridRef,
    filterChipRef,
    onExportCSV,
    selectedExtraction,
    onClosePdfViewer,
    isProjectArchived
  ]);

  const statusBudge = useMemo(
    () =>
      getGridStatusBudge(
        {
          title: "Materials",
          totalCount: pageData.entityList?.length,
          filterStats: statsData
        },
        onAllMaterialClick
      ),
    [onAllMaterialClick, pageData.entityList?.length, statsData]
  );

  const extractedMaterialsTitleDropdown = useMemo(
    () => (
      <div
        className="flex items-center justify-start cursor-pointer"
        data-testid="material-header-title"
      >
        <CiqIconBox feature="EXTRACTED_MATERIALS" size={20} padding={8} />
        <div className="flex flex-col pl-3">
          <div className="flex items-center space-x-2">
            <Dropdown
              menu={materialsTypeMenu}
              trigger={["hover"]}
              placement="bottomLeft"
            >
              <span className="text-black text-xl font-semibold">
                Extracted Materials
              </span>
            </Dropdown>
            <DownOutlined className="text-xs self-center mt-0.5" />
            {statusBudge && (
              <div
                className="flex shrink-0 ml-2"
                data-testid="material-header-stats"
              >
                {statusBudge}
              </div>
            )}
          </div>
          {extractionHistory?.some((item) => item.status === "IN_PROGRESS") && (
            <div className="text-xs text-ciq-gray-700 font-normal">
              Extraction in Progress...
            </div>
          )}
        </div>
      </div>
    ),
    [materialsTypeMenu, statusBudge, extractionHistory]
  );

  return (
    <div className="w-full px-6 py-4 flex justify-between items-center">
      <div className="flex space-x-2 items-center text-black">
        <Space
          className="flex items-center justify-center cursor-pointer p-1"
          onClick={() => history.replace(`/project/${projectId}/materials`)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ")
              history.replace(`/project/${projectId}/materials`);
          }}
          role="button"
          tabIndex={0}
          aria-label="Back to Materials"
          data-testid="material-header-back-btn"
        >
          <ArrowLeftOutlined />
        </Space>
        {extractedMaterialsTitleDropdown}
      </div>

      <div className="flex space-x-2 items-center">{headerItems}</div>
      {openUploadDrawingModal && (
        <ImportExtractionDrawing
          openDrawer={openUploadDrawingModal}
          onClose={() => {
            setOpenUploadDrawingModal(false);
            gridRef.current?.api.deselectAll();
          }}
          extractionHistoryData={extractionHistory}
          refetchExtractionHistoryData={pageData.refetchExtractionHistoryData}
        />
      )}
      {openCreateMaterial && selectedRows && selectedRows.length > 0 && (
        <CreateMaterialModal
          open={openCreateMaterial}
          onCancel={() => setOpenCreateMaterial(false)}
          onComplete={() => {
            setOpenCreateMaterial(false);
            gridRef.current?.api.deselectAll();
          }}
          selectedRow={selectedRows[0]}
          materialHeaderMap={pageData.materialHeaderMap}
        />
      )}
      {openCreateMaterialsMulti && selectedRows && selectedRows.length > 0 && (
        <CreateMaterialsMultiModal
          open={openCreateMaterialsMulti}
          onCancel={() => setOpenCreateMaterialsMulti(false)}
          onComplete={() => {
            setOpenCreateMaterialsMulti(false);
            gridRef.current?.api.deselectAll();
          }}
          selectedRows={selectedRows}
          materialHeaderMap={pageData.materialHeaderMap}
        />
      )}
      {openMergeCreate && selectedRows && selectedRows.length > 0 && (
        <MergeCreateMaterialModal
          open={openMergeCreate}
          onCancel={() => setOpenMergeCreate(false)}
          onComplete={() => {
            setOpenMergeCreate(false);
            gridRef.current?.api.deselectAll();
          }}
          selectedRows={selectedRows}
          materialHeaderMap={pageData.materialHeaderMap}
        />
      )}
      {openHistorySidebar && (
        <ExtractionHistoryGridModal
          open={openHistorySidebar}
          onClose={() => setOpenHistorySidebar(false)}
          extractionHistory={extractionHistory || []}
        />
      )}
    </div>
  );
}
